import React, { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../store';
import { useStatementsListQuery } from '../../../hooks/query/useStatementsListQuery';
import ContentWrapper from '../../../generic-components/ContentWrapper';
import SubContent from '../../../generic-components/Layout/SubContent';
import YearFilter from '../../../generic-components/YearFilter';
import MainContent from '../../../generic-components/Layout/MainContent';
import DocsArea from '../../../generic-components/DocsArea';
import { getExistStatementsYears } from '../../../helpers/getYearsArrayFromCreationTime.helper';
import TwoChoiceToggler from '../../../generic-components/TwoChoiceToggler';
import { FILTER_OPTIONS, NO_DROP_DOWN } from '../constants';
import { PROFILE_TYPES, STATEMENTS_PAGE_HEADER } from '../../../constants/general';
import 'react-loading-skeleton/dist/skeleton.css';
import StatementExpensesSkeleton from '../../../generic-components/StatementExpensesSkeleton';
import { Layout } from '../../../components/Layout/Layout';
import Heading from '../../../generic-components/Heading';

export interface IStatement {
  externalId: string;
  customerId: string;
  createdAt: string;
  path: string;
  viewed: boolean;
  title: string;
  statementTitle: string;
  accountType: string;
  year: number;
  month: number;
}

interface IMainAccount {
  year: number;
  title: string;
  statements: IStatement[];
}

const { MAIN_ACCOUNT, BUCKETS } = FILTER_OPTIONS;

const Statements = () => {
  const { customerId } = useStore((state) => state.currUser());
  const {
    customer: { customerProfile },
  } = useStore((state) => state.getSummaryData());
  const { data: statementsData, isLoading, refetch } = useStatementsListQuery({ customerId });
  const [yearsArr, setYearsArr] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(0);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<string>(MAIN_ACCOUNT);
  const [selectedSubOptionIndex, setSelectedSubOptionIndex] = useState<number>(0);
  const selectionArr = useMemo(() => {
    switch (customerProfile) {
      case PROFILE_TYPES.BASIC_V2:
      case PROFILE_TYPES.BASIC:
        return ['Checking', 'Buckets'];
      default:
        return ['Checking', 'Savings'];
    }
  }, [customerProfile]);

  const dropdownArr = useMemo(() => [], [selectedYear]);

  const activeYearIdx = useMemo(() => {
    const idx = statementsData?.mainAccount?.findIndex((i: IMainAccount) => i.year === selectedYear);
    if (idx === -1) return 0;
    return idx;
  }, [selectedYear]);
  useEffect(() => {
    if (dropdownArr.length === 1) {
      setSelectedSubOptionIndex(0);
    }
  }, [dropdownArr]);

  useEffect(() => {
    setSelectedYear(yearsArr[0]);
  }, [yearsArr]);

  useEffect(() => {
    setYearsArr(getExistStatementsYears(statementsData));
  }, [statementsData]);

  useEffect(() => {
    refetch();
  }, [selectedYear]);
  const { t } = useTranslation();

  return (
    <Layout wrapperClassName='pt-[78px] pl-[72px] pr-[32px] ml-[100px] pc-md:ml-[256px]'>
      <Heading title={t(STATEMENTS_PAGE_HEADER)} className='font-metricRSemibold text-bw08 text-[20px] mb-[24px]' />
      <ContentWrapper>
        <SubContent className='flex flex-col items-center bg-bw00 rounded-xl w-[264px] px-[40px] py-[1.875rem]'>
          <TwoChoiceToggler
            toggleState={selectedOptionIndex}
            setToggleState={setSelectedOptionIndex}
            options={selectionArr}
          />
          {isLoading ? (
            <div className='w-full h-full'>
              <Skeleton className='my-4' count={9} />
            </div>
          ) : (
            <YearFilter
              yearsToDisplay={yearsArr}
              currentYear={selectedYear}
              setCurrentYear={setSelectedYear}
              dropdownList={null}
              subOptionIndex={
                dropdownArr.length === 0 && selectedOptionIndex === BUCKETS ? NO_DROP_DOWN : selectedSubOptionIndex
              }
              setSubOptionIndex={setSelectedSubOptionIndex}
            />
          )}
        </SubContent>
        <MainContent className='flex flex-col bg-bw00 rounded-xl w-[840px] pl-10 pr-[38px] pt-10 pb-[34px] h-[80vh] max-h-[80vh] overflow-auto'>
          {statementsData && !isLoading && (
            <DocsArea
              title={`${selectedYear}`}
              year={selectedYear}
              docs={
                selectedOptionIndex === BUCKETS
                  ? statementsData.buckets?.[activeYearIdx]?.statements
                  : statementsData?.mainAccount?.[activeYearIdx]?.statements
              }
              expenses={false}
            />
          )}
          {isLoading && <StatementExpensesSkeleton />}
        </MainContent>
      </ContentWrapper>
    </Layout>
  );
};

export default Statements;
